import { useCallback, useEffect, useRef } from "react";
import type { AudioVideoFacade } from "amazon-chime-sdk-js";
import { DEVICE_STATE_HEARTBEAT_MS } from "@core/features/video";

const DEVICE_STATE_TOPIC = "device-state";
const MESSAGE_LIFETIME_MS = 30_000;

interface DeviceStateMessage {
  camera: boolean;
  mic: boolean;
  timestamp: number;
}

/**
 * Broadcasts local device state (camera/mic) to remote attendees via the
 * Chime data message channel. Handles change broadcasts, late-joiner
 * re-broadcasts, and a periodic heartbeat.
 */
export function useDeviceStateBroadcast(
  audioVideo: AudioVideoFacade | null,
  hasLocalVideo: boolean,
  muted: boolean,
  rosterSize: number
): { broadcast: () => void } {
  const deviceStateRef = useRef({ hasLocalVideo, muted });
  useEffect(() => {
    deviceStateRef.current = { hasLocalVideo, muted };
  }, [hasLocalVideo, muted]);

  const broadcast = useCallback(() => {
    if (!audioVideo) return;
    const { hasLocalVideo, muted } = deviceStateRef.current;
    const localTile = audioVideo.getLocalVideoTile();
    const actualCamera = localTile?.state()?.active ?? false;

    try {
      audioVideo.realtimeSendDataMessage(
        DEVICE_STATE_TOPIC,
        JSON.stringify({
          camera: hasLocalVideo && actualCamera,
          mic: !muted,
          timestamp: Date.now(),
        } satisfies DeviceStateMessage),
        MESSAGE_LIFETIME_MS
      );
    } catch (e) {
      if (process.env.NODE_ENV === 'development') {
        console.debug('[useDeviceStateBroadcast] data message send failed:', e);
      }
    }
  }, [audioVideo]);

  // Broadcast on camera/mic change
  useEffect(() => {
    broadcast();
  }, [hasLocalVideo, muted, broadcast]);

  // Re-broadcast on roster size change (late joiners), debounced 1s
  useEffect(() => {
    const timer = setTimeout(broadcast, 1_000);
    return () => clearTimeout(timer);
  }, [rosterSize, broadcast]);

  // Heartbeat — re-broadcast every DEVICE_STATE_HEARTBEAT_MS
  useEffect(() => {
    if (!audioVideo) return;
    const interval = setInterval(broadcast, DEVICE_STATE_HEARTBEAT_MS);
    return () => clearInterval(interval);
  }, [audioVideo, broadcast]);

  return { broadcast };
}
